import type { Locale } from 'date-fns';
import {
  differenceInCalendarWeeks,
  endOfMonth,
  format,
  isAfter,
  isBefore,
  isMatch,
  isSameDay,
  isValid,
  parse,
  startOfDay,
  sub,
} from 'date-fns';
import { enUS, ptBR } from 'date-fns/locale';

import type { TDatePeriod } from 'watchtower-ui/components/Filter/constants';
import { DAY_NUMBER_MAP, MONTHLY_PERIOD } from 'watchtower-ui/types/date';
import type { TDateParam, TDay } from 'watchtower-ui/types/date';

const defaultDateFormat = 'yyyy-MM-dd';

const SAMPLE_YEAR = 2000;

export const defaultMonthFormat = 'MMM-yyyy';

export const formatDate = (date: Date, dateFormat: string = defaultDateFormat) => format(date, dateFormat);

export const isSaturday = (date: Date): boolean => date.getDay() === 6;

const convertLocalDateToUTCIgnoringTimezone = (date: Date) =>
  new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
      date.getMilliseconds(),
    ),
  );

// Map of supported locales
export const supportedLocales: { [key: string]: Locale } = {
  'en-US': enUS,
  'pt-BR': ptBR,
};

// Helper to get the best matching browser locale
export const getBrowserLocale = (): string => {
  const browserLocale = navigator.languages?.[0] || navigator.language || 'en-US';
  // Use exact match if available; otherwise, match language part.
  if (supportedLocales[browserLocale]) return browserLocale;
  const languageOnly = browserLocale.split('-')[0];
  for (const key in supportedLocales) {
    if (languageOnly && key.startsWith(languageOnly)) return key;
  }
  return 'en-US';
};

export const parseDateString = (dateString: string, moveToUTC?: boolean): Date | null => {
  let formattedDateString = dateString;

  // Check if dateString is in the format 'MMM-yyyy', and if so, convert it to the last day of the month
  if (isMatch(dateString, defaultMonthFormat)) {
    formattedDateString = formatDate(endOfMonth(parse(dateString, defaultMonthFormat, new Date())));
  }

  const delimiter = dateString.includes('/') ? '/' : '-';
  const [first, second, third] = formattedDateString.split(delimiter).map((s) => parseInt(s, 10));

  let year;
  let month;
  let day;
  if (delimiter === '/') {
    [month, day, year] = [first, second, third];
  } else {
    [year, month, day] = [first, second, third];
  }

  // Check if year, month, and day are valid integers
  if (!Number.isSafeInteger(year) || !Number.isSafeInteger(month) || !Number.isSafeInteger(day)) return null;

  // Ensure year, month, and day are not undefined before creating a new Date
  if (year === undefined || month === undefined || day === undefined) return null;

  const date = new Date(year, month - 1, day);
  if (moveToUTC) {
    return convertLocalDateToUTCIgnoringTimezone(date);
  } else {
    return date;
  }
};

export const getFirstWeek = (selectedDate: Date) => {
  // We are copying the date so we can modify it.
  const result = new Date(selectedDate);
  result.setMonth(0, 1);
  result.setDate(7 - result.getDay());
  return result;
};

export const yearBackDay = (endDate: Date) => {
  const date = sub(endDate, { years: 1 });
  const lastDay = date.getDate() - (date.getDay() - 1) + 5;
  return new Date(date.setDate(lastDay));
};

// made the changes as the test should pass in every time zone, so date will be coneverted into UTC and then checked
export const isDateInRange = (date: Date, minDate: Date, maxDate: Date): boolean => {
  const utcDate = startOfDay(date).getTime();
  const utcMinDate = startOfDay(minDate).getTime();
  const utcMaxDate = startOfDay(maxDate).getTime();

  return (
    isSameDay(date, minDate) ||
    isSameDay(date, maxDate) ||
    (isAfter(utcDate, utcMinDate) && isBefore(utcDate, utcMaxDate) && isSaturday(date))
  );
};
export const isValidDate = (date: unknown): date is Date => isValid(date) && date instanceof Date;

export const checkDiffInWeeks = (
  startDate: Date | null | undefined,
  endDate: Date | null | undefined,
): number | undefined => {
  if (startDate && endDate) {
    // here 1 we are adding because we are comparing with saturday's so everytime it will one less count
    return differenceInCalendarWeeks(endDate, startDate) + 1;
  }
  return undefined;
};

/**
 * ### getPeriodTypeFromDate
 * Given a date (Date object or date string), returns an object containing the period representations:
 * - monthly: 'Jan', 'Feb', etc.
 * - quarterly: 'Q1', 'Q2', etc.
 * - halfyearly: 'H1', 'H2'
 * - yearly: '2023', '2024', etc.
 * - custom: full date string representation
 *
 * Returns null if the date is invalid or not provided.
 */
export const getPeriodTypeFromDate = (date: TDateParam): Record<TDatePeriod, string> | null => {
  if (!date) {
    return null;
  }

  try {
    if (typeof date === 'string' || typeof date === 'number') {
      date = new Date(date);
    }

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    const month = date.getMonth();

    return {
      monthly: MONTHLY_PERIOD[month]!,
      quarterly: `Q${Math.floor(month / 3) + 1}`,
      halfyearly: month < 6 ? 'H1' : 'H2',
      ytd: date.getFullYear().toString(),
      custom: date.toDateString(),
    };
  } catch (e) {
    console.error('Error parsing date for period selector:', e);
    return null;
  }
};

/**
 * ### getYearFromDate
 * Extracts the year from a given date object or string.
 * Returns null if the date is invalid or not provided.
 */
export const getYearFromDate = (date: TDateParam): number | null => {
  if (!date) {
    return null;
  }

  try {
    date = new Date(date);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return date.getFullYear();
  } catch (e) {
    console.error('Error parsing date for getYearFromDate:', e);
    return null;
  }
};

/**
 * ### getNextSaturday
 * Given a date, returns the date of the next Saturday (inclusive).
 */
export const getNextSaturday = (date: TDateParam): Date | null => {
  if (!date) {
    return null;
  }

  try {
    date = new Date(date);

    const dayOfWeek = date.getDay();
    const daysToAdd = (6 - dayOfWeek + 7) % 7;
    const nextSaturday = new Date(date);
    nextSaturday.setDate(date.getDate() + daysToAdd);

    return nextSaturday;
  } catch (e) {
    console.error('Error parsing date for getNextSaturday:', e);
    return null;
  }
};

/**
 * ### getPreviousSaturday
 * Given a date, returns the date of the previous Saturday (inclusive).
 */
export const getPreviousSaturday = (date: TDateParam): Date | null => {
  if (!date) {
    return null;
  }

  try {
    date = new Date(date);

    const dayOfWeek = date.getDay();
    const daysToSubtract = (dayOfWeek + 1) % 7;
    const previousSaturday = new Date(date);
    previousSaturday.setDate(date.getDate() - daysToSubtract);

    return previousSaturday;
  } catch (e) {
    console.error('Error parsing date for getPreviousSaturday:', e);
    return null;
  }
};

/**
 * ### checkIfValidDateRange
 * Validates if the provided value array form a valid date range.
 * Returns true if both dates are valid.
 */
export const checkIfValidDateRange = (value?: unknown): boolean => {
  if (!value || !Array.isArray(value) || value.length !== 2) {
    return false;
  }

  try {
    const [start, end] = value as [string | Date, string | Date];
    const startDate = new Date(start);
    const endDate = new Date(end);

    return isValid(startDate) && isValid(endDate);
  } catch (e) {
    console.error('Error validating date range:', e);
    return false;
  }
};

/**
 * ### shortDate
 * Formats a date into a short string format: DD-MMM'YY (e.g., 05-Jan'24).
 */
export const shortDate = (date?: TDateParam) => {
  if (!date) {
    return '';
  }

  try {
    if (typeof date === 'string' || typeof date === 'number') {
      date = new Date(date);
    }

    // Check if the date is valid before formatting
    if (!isValid(date)) {
      return '';
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('default', { month: 'short' });
    const year = String(date.getFullYear()).slice(-2);
    return `${day}-${month}'${year}`;
  } catch (e) {
    console.error('Error parsing date for shortDate:', e);
    return '';
  }
};

/**
 * ### getMonthIndex
 * Given a month name (e.g., "January") and an optional locale code, returns the corresponding month index (0 for January, 1 for February, etc.).
 * If the month name is not found, returns -1.
 */
export const getMonthIndex = (monthName: string, localeCode: string = 'en-US'): number => {
  const locale = supportedLocales[localeCode] || enUS;
  const date = new Date(SAMPLE_YEAR, 0, 1);
  for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
    date.setMonth(monthIndex);
    const formattedMonthName = format(date, 'LLLL', { locale });
    if (formattedMonthName.toLowerCase() === monthName.toLowerCase()) {
      return monthIndex;
    }
  }
  return -1;
};

/**
 * ### getMonthName
 * Given a month index (0 for January, 1 for February, etc.) and an optional locale code, returns the corresponding month name (e.g., "January").
 * If the month index is out of range, returns an empty string.
 */
export const getMonthName = (monthIndex: number, localeCode: string = 'en-US'): string => {
  const locale = supportedLocales[localeCode] || enUS;
  const date = new Date(SAMPLE_YEAR, monthIndex, 1);
  return format(date, 'LLLL', { locale });
};

/**
 * ### getMonthRange
 * Given optional minimum and maximum date parameters, returns an array of month names that fall within the specified range.
 * The function handles both Date objects and numeric month indices as input.
 * If the range exceeds 12 months, it returns all month names.
 */
export const getMonthRange = (minDate?: TDateParam, maxDate?: TDateParam): string[] => {
  const months: string[] = [];

  // Find the difference in dates. If more than 12 months, then return all months

  if (!minDate || !maxDate) {
    return months;
  }

  if (minDate > maxDate) {
    return months;
  }

  let minMonth = 0;
  let maxMonth = 11;

  if (Number.isInteger(minDate) && Number.isInteger(maxDate)) {
    minMonth = minDate as number;
    maxMonth = maxDate as number;
  }

  if (maxDate instanceof Date && minDate instanceof Date) {
    let monthDiff = maxDate.getFullYear() - minDate.getFullYear();
    monthDiff *= 12;
    monthDiff += maxDate.getMonth();
    monthDiff -= minDate.getMonth();

    if (monthDiff < 12) {
      minMonth = minDate.getMonth();
      maxMonth = maxDate.getMonth();
    }
  }

  for (let month = minMonth; month <= maxMonth; month++) {
    months.push(getMonthName(month, 'en-US'));
  }

  return months;
};

/**
 * ### getYearRange
 * Given optional minimum and maximum date parameters, returns an array of years that fall within the specified range.
 * The function handles both Date objects and numeric year values as input.
 * If the range is invalid (e.g., minDate is after maxDate), it returns an empty array.
 */
export const getYearRange = (minDate?: TDateParam, maxDate?: TDateParam): number[] => {
  const years: number[] = [];

  if (!minDate || !maxDate) {
    return years;
  }

  if (minDate instanceof Date) {
    minDate = minDate.getFullYear();
  }

  if (maxDate instanceof Date) {
    maxDate = maxDate.getFullYear();
  }

  if (minDate > maxDate) {
    return years;
  }

  for (let year = Number(minDate); year <= Number(maxDate); year++) {
    years.push(year);
  }

  return years;
};

/**
 * ### getDatePeriodRangeFromDate
 * Given a period type and a date, returns the [startDate, endDate] of the
 * period block that contains that date.
 *
 * - monthly:    first → last day of the month
 * - quarterly:  first → last day of the quarter (Q1–Q4)
 * - halfyearly: first → last day of the half-year (H1/H2)
 * - ytd:        Jan 1 → end of given date
 * - custom:     start of given date → end of given date
 */
export const getDatePeriodRangeFromDate = (period: TDatePeriod, date: Date | string): [Date, Date] => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = d.getMonth();

  if (period === 'monthly') {
    return [new Date(year, month, 1), new Date(year, month + 1, 0, 23, 59, 59, 999)];
  }

  if (period === 'quarterly') {
    const startMonth = Math.floor(month / 3) * 3;
    return [new Date(year, startMonth, 1), new Date(year, startMonth + 3, 0, 23, 59, 59, 999)];
  }

  if (period === 'halfyearly') {
    const startMonth = month < 6 ? 0 : 6;
    return [new Date(year, startMonth, 1), new Date(year, startMonth + 6, 0, 23, 59, 59, 999)];
  }

  if (period === 'ytd') {
    return [new Date(year, 0, 1), new Date(year, month, d.getDate(), 23, 59, 59, 999)];
  }

  return [new Date(year, month, d.getDate()), new Date(year, month, d.getDate(), 23, 59, 59, 999)];
};

/**
 * ### isDateDisabled
 * Returns true (disabled) if the given date — or a period block [date, endDate] —
 * falls outside the allowed [minDate, maxDate] window.
 *
 * When `endDate` is supplied the check requires the ENTIRE block to fit:
 *   • block start (date)      must be >= minDate
 *   • block end  (endDate)  must be <= maxDate
 */
export const isDateDisabled = (
  date: Date,
  options: {
    minDate?: Date;
    maxDate?: Date;
    endDate?: Date;
  },
) => {
  const { minDate, maxDate, endDate = date } = options;

  if (minDate && !isSameDay(date, minDate) && !isAfter(date.getTime(), minDate.getTime())) {
    return true;
  }

  if (maxDate && !isSameDay(endDate, maxDate) && !isBefore(endDate.getTime(), maxDate.getTime())) {
    return true;
  }

  return false;
};

export const maskTimeBlockFromStartDay = (
  startDay: TDay | null | undefined,
  dateRange: [Date | string, Date | string] | null | undefined,
): [Date, Date] => {
  if (!dateRange) return [new Date(), new Date()];
  const [startDate, endDate] = dateRange;
  // Use parseDateString for string inputs to avoid UTC-midnight timezone shift
  const maskedStartDate =
    typeof startDate === 'string' ? (parseDateString(startDate) ?? new Date(startDate)) : new Date(startDate);
  const maskedEndDate =
    typeof endDate === 'string' ? (parseDateString(endDate) ?? new Date(endDate)) : new Date(endDate);

  if (!startDay) {
    return [maskedStartDate, maskedEndDate];
  }

  const targetDayNumber = DAY_NUMBER_MAP[startDay];
  const firstSaturday = getPreviousSaturday(maskedStartDate);
  const lastSaturday = getPreviousSaturday(maskedEndDate);

  // We start ranging between first and last saturday from the given date range
  // Then we work back to match the starting day block.
  if (firstSaturday && isBefore(firstSaturday, maskedStartDate)) {
    maskedStartDate.setTime(firstSaturday.getTime());
  }

  if (lastSaturday && isAfter(lastSaturday, maskedEndDate)) {
    maskedEndDate.setTime(lastSaturday.getTime());
  }

  // Mask the start date
  const daysBack = (maskedStartDate.getDay() - targetDayNumber + 7) % 7;
  maskedStartDate.setDate(maskedStartDate.getDate() - daysBack);

  // Mask the end date
  const daysForward = (((targetDayNumber + 6) % 7) - maskedEndDate.getDay() + 7) % 7;
  maskedEndDate.setDate(maskedEndDate.getDate() + daysForward);

  return [maskedStartDate, maskedEndDate];
};
